import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { getPrimaryYear, getMaxVisibleDepth, formatYear } from '../utils/dateUtils';
import { CATEGORY_COLORS } from '../constants';
import '../styles/TimelineRenderer.css';

/**
 * Minimum horizontal distance (in %) between events before they stack vertically.
 * This is dynamically calculated based on zoom level.
 */
const BASE_OVERLAP_THRESHOLD = 6;

/**
 * Vertical spacing between stacked events (in px).
 */
const STACK_OFFSET_PX = 65;

/**
 * TimelineRenderer - Main zoomable timeline visualization component.
 * Renders events positioned along a horizontal time axis with connector lines
 * and vertical stacking to avoid overlaps.
 */
function TimelineRenderer({ events = [], visibleRange, maxDepth, onEventClick, onRangeChange }) {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartRange, setDragStartRange] = useState(null);

  const rangeSize = visibleRange.end - visibleRange.start;

  const getEventColor = useCallback((event) => {
    if (event.color) return event.color;
    return CATEGORY_COLORS[event.category] || CATEGORY_COLORS.world;
  }, []);

  const getPositionPercent = useCallback((event) => {
    const year = getPrimaryYear(event);
    return ((year - visibleRange.start) / rangeSize) * 100;
  }, [visibleRange.start, rangeSize]);

  // All events visible regardless of zoom
  const visibleEvents = events;

  // Sort events by primary year for overlap detection
  // ALL events get stalks below the axis
  const sortedEvents = useMemo(() => {
    return [...visibleEvents].sort((a, b) => getPrimaryYear(a) - getPrimaryYear(b));
  }, [visibleEvents]);

  /**
   * Calculate layout positions with vertical stacking for overlapping events.
   * Returns an array of { event, posPercent, row } objects.
   */
  const layoutEvents = useMemo(() => {
    const placed = [];

    // Wider threshold when zoomed out (more years visible = tighter spacing)
    // At 6000 years visible, threshold ~8%. At 100 years, threshold ~4%.
    const overlapThreshold = Math.max(BASE_OVERLAP_THRESHOLD, Math.min(12, rangeSize / 600));

    for (const event of sortedEvents) {
      const posPercent = getPositionPercent(event);

      // Skip events far outside visible bounds
      if (posPercent < -10 || posPercent > 110) continue;

      // Find which row this event should go on to avoid overlaps
      let row = 0;
      let foundSlot = false;
      while (!foundSlot) {
        const conflict = placed.find(
          (p) => p.row === row && Math.abs(p.posPercent - posPercent) < overlapThreshold
        );
        if (conflict) {
          row++;
        } else {
          foundSlot = true;
        }
      }

      placed.push({ event, posPercent, row });
    }

    return placed;
  }, [sortedEvents, getPositionPercent, rangeSize]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const cursorXPercent = (e.clientX - rect.left) / rect.width;

    const zoomFactor = e.deltaY > 0 ? 1.2 : 0.8;
    const newRangeSize = Math.max(10, Math.round(rangeSize * zoomFactor));

    const cursorYear = visibleRange.start + cursorXPercent * rangeSize;
    const newStart = Math.round(cursorYear - cursorXPercent * newRangeSize);
    const newEnd = newStart + newRangeSize;

    if (onRangeChange) onRangeChange(newStart, newEnd);
  }, [rangeSize, visibleRange.start, onRangeChange]);

  // Handle drag-to-pan
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.tlr-event-node')) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartRange({ start: visibleRange.start, end: visibleRange.end });
  }, [visibleRange]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragStartRange) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const dx = e.clientX - dragStartX;
    const yearsDelta = -(dx / rect.width) * rangeSize;

    const newStart = Math.round(dragStartRange.start + yearsDelta);
    const newEnd = Math.round(dragStartRange.end + yearsDelta);

    if (onRangeChange) onRangeChange(newStart, newEnd);
  }, [isDragging, dragStartX, dragStartRange, rangeSize, onRangeChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStartRange(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => { container.removeEventListener('wheel', handleWheel); };
  }, [handleWheel]);

  // Touch support for mobile pan and pinch-zoom
  const touchRef = useRef({ startX: 0, startRange: null, pinchDist: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchRef.current.startX = e.touches[0].clientX;
        touchRef.current.startRange = { start: visibleRange.start, end: visibleRange.end };
      } else if (e.touches.length === 2) {
        touchRef.current.pinchDist = getDistance(e.touches[0], e.touches[1]);
        touchRef.current.startRange = { start: visibleRange.start, end: visibleRange.end };
      }
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();

      if (e.touches.length === 1 && touchRef.current.startRange) {
        // Single finger pan
        const dx = e.touches[0].clientX - touchRef.current.startX;
        const yearsDelta = -(dx / rect.width) * rangeSize;
        const newStart = Math.round(touchRef.current.startRange.start + yearsDelta);
        const newEnd = Math.round(touchRef.current.startRange.end + yearsDelta);
        if (onRangeChange) onRangeChange(newStart, newEnd);
      } else if (e.touches.length === 2 && touchRef.current.startRange) {
        // Pinch zoom
        const newDist = getDistance(e.touches[0], e.touches[1]);
        const scale = touchRef.current.pinchDist / newDist;
        const startRange = touchRef.current.startRange;
        const oldSize = startRange.end - startRange.start;
        const newSize = Math.max(10, Math.round(oldSize * scale));
        const center = startRange.start + oldSize / 2;
        const newStart = Math.round(center - newSize / 2);
        const newEnd = newStart + newSize;
        if (onRangeChange) onRangeChange(newStart, newEnd);
      }
    };

    const handleTouchEnd = () => {
      touchRef.current.startRange = null;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [visibleRange, rangeSize, onRangeChange]);

  const getDepthClass = (depth) => {
    switch (depth) {
      case 0: return 'tlr-depth-era';
      case 1: return 'tlr-depth-period';
      case 2: return 'tlr-depth-event';
      case 3: return 'tlr-depth-subevent';
      default: return 'tlr-depth-event';
    }
  };

  const handleEventClick = useCallback((event) => {
    if (onEventClick) onEventClick(event);
  }, [onEventClick]);

  // Build span bar layout (ranged events — above axis)
  const spanLayout = useMemo(() => {
    const spanEvents = events.filter(e => e.endYear);
    const spanBars = [];
    const sorted = [...spanEvents].sort((a, b) => getPrimaryYear(a) - getPrimaryYear(b));

    for (const event of sorted) {
      // Raw (unclamped) positions along the axis
      const rawStartPercent = ((event.startYear - visibleRange.start) / rangeSize) * 100;
      const rawEndPercent = ((event.endYear - visibleRange.start) / rangeSize) * 100;

      // Skip only if the whole span is off-screen. If part of it is visible,
      // keep it and let the clamped values below clip it at the edges.
      if (rawEndPercent < 0 || rawStartPercent > 100) continue;

      // Clamp to viewport for rendering so off-screen portions are simply cut off
      const startPercent = Math.max(0, rawStartPercent);
      const endPercent = Math.min(100, rawEndPercent);
      const widthPercent = endPercent - startPercent;

      if (widthPercent < 0.3) continue;

      let row = 0;
      let foundSlot = false;
      while (!foundSlot) {
        const conflict = spanBars.find(
          (b) => b.row === row && !(endPercent <= b.startPercent || startPercent >= b.endPercent)
        );
        if (conflict) row++;
        else foundSlot = true;
      }

      spanBars.push({ event, startPercent, endPercent, widthPercent, rawStartPercent, rawEndPercent, row });
    }
    return spanBars;
  }, [events, visibleRange.start, rangeSize]);

  const spanMaxRow = spanLayout.reduce((max, item) => Math.max(max, item.row), 0);
  const spanAreaHeight = (spanMaxRow + 1) * 28 + 8;

  // Calculate below-axis container height
  const maxRow = layoutEvents.reduce((max, item) => Math.max(max, item.row), 0);
  const belowAxisHeight = Math.max(100, 30 + (maxRow + 1) * STACK_OFFSET_PX);

  return (
    <div
      className={`tlr-container ${isDragging ? 'tlr-dragging' : ''}`}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      role="region"
      aria-label="Timeline visualization"
    >
      {/* ABOVE AXIS: Span bars with connector lines up from axis */}
      <div className="tlr-above-axis" style={{ height: `${spanAreaHeight}px` }}>
        {spanLayout.map(({ event, startPercent, endPercent, widthPercent, rawStartPercent, rawEndPercent, row }) => {
          const color = getEventColor(event);
          const markerLeft = rawStartPercent;
          // Bar bottom position (row 0 closest to axis)
          const barBottom = row * 28 + 4;
          // Connector goes from the very bottom (axis level) up to the bar
          const connectorHeight = barBottom;
          // Only show a start/end marker + its date when that endpoint is actually on-screen.
          // The bar itself stays visible (clipped) even when an endpoint is scrolled off.
          const startInView = rawStartPercent >= 0 && rawStartPercent <= 100;
          const endInView = rawEndPercent >= 0 && rawEndPercent <= 100;

          return (
            <div key={`span-${event.id}`}>
              {/* The span bar */}
              <div
                className="tlr-span-bar"
                style={{
                  left: `${startPercent}%`,
                  width: `${widthPercent}%`,
                  bottom: `${barBottom}px`,
                  backgroundColor: color,
                }}
                onClick={() => handleEventClick(event)}
                role="button"
                tabIndex={0}
                aria-label={`${event.title}: ${formatYear(event.startYear)} – ${formatYear(event.endYear)}`}
              >
                {startInView && (
                  <span className="tlr-span-date tlr-span-date-start">{formatYear(event.startYear)}</span>
                )}
                <span className="tlr-span-label">{event.title}</span>
                {endInView && (
                  <span className="tlr-span-date tlr-span-date-end">{formatYear(event.endYear)}</span>
                )}
              </div>
              {/* Connector line from axis up to bar */}
              {startInView && connectorHeight > 0 && (
                <div
                  className="tlr-span-connector"
                  style={{
                    left: `${markerLeft}%`,
                    height: `${connectorHeight}px`,
                    backgroundColor: color,
                  }}
                />
              )}
              {/* Connector line from axis up to bar END */}
              {endInView && connectorHeight > 0 && (
                <div
                  className="tlr-span-connector"
                  style={{
                    left: `${endPercent}%`,
                    height: `${connectorHeight}px`,
                    backgroundColor: color,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* AXIS LINE */}
      <div className="tlr-axis">
        <div className="tlr-axis-line" />
      </div>

      {/* BELOW AXIS: Point events with connector lines */}
      <div className="tlr-below-axis" style={{ height: `${belowAxisHeight}px` }}>
        {layoutEvents.map(({ event, posPercent, row }) => {
          const color = getEventColor(event);
          const depth = event.depth != null ? event.depth : 2;
          const depthClass = getDepthClass(depth);
          const isChild = !!event.parentId;
          const connectorHeight = 15 + row * STACK_OFFSET_PX;

          return (
            <div key={event.id} className="tlr-event-wrapper" style={{ left: `${posPercent}%` }}>
              <div
                className="tlr-connector-line"
                style={{ height: `${connectorHeight}px`, backgroundColor: color }}
              />
              <div
                className={`tlr-event-node ${depthClass} ${isChild ? 'tlr-child-event' : ''}`}
                onClick={() => handleEventClick(event)}
                role="button"
                tabIndex={0}
                aria-label={`Event: ${event.title}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEventClick(event);
                  }
                }}
              >
                <div className="tlr-event-marker" style={{ backgroundColor: color }} />
                <div className="tlr-event-title">{event.title}</div>
                <div className="tlr-event-year">{formatYear(getPrimaryYear(event))}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TimelineRenderer;
